import React from 'react';
import { Badge, IconButton, useTheme, keyframes } from '@mui/material';
import { Notifications } from '@mui/icons-material';

interface NotificationBellProps {
  unreadCount: number;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

// Pulse animation for new notifications
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
`;

const NotificationBell: React.FC<NotificationBellProps> = ({ unreadCount, onClick }) => {
  const theme = useTheme();

  return (
    <Badge
      badgeContent={unreadCount > 99 ? '99+' : unreadCount}
      color="error"
      invisible={unreadCount === 0}
      sx={{
        '& .MuiBadge-badge': {
          right: { xs: 2, sm: 4 },
          top: { xs: 2, sm: 4 },
          fontSize: { xs: '0.6rem', sm: '0.75rem' },
          minWidth: { xs: 16, sm: 18 },
          height: { xs: 16, sm: 18 },
          animation: unreadCount > 0 ? `${pulse} 2s infinite` : 'none',
          fontWeight: 600
        }
      }}
    >
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={`${unreadCount} unread notifications`}
        sx={{
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.action.hover,
          width: { xs: 36, sm: 44, md: 48 },
          height: { xs: 36, sm: 44, md: 48 },
          minWidth: { xs: 36, sm: 44, md: 48 },
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: theme.palette.action.selected,
            transform: 'scale(1.05)'
          }
        }}
      >
        <Notifications sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' } }} />
      </IconButton>
    </Badge>
  );
};

export default NotificationBell;
